import React, { FC, useCallback, useMemo } from 'react';

import { EvSection, EvTitle } from '@evinced-private/ui-common';

import { MOBILE_SDK_DASHBOARD_PAGE_TABLE_SECTION_ID } from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import { IMobileSdkTestRow } from 'src/apps/mobile-sdk/types/MobileSdkServiceTypes';
import { LabelsManagementTableActions } from 'src/common/components/labels-management-table-actions';
import { useMobileConfiguredValues } from 'src/common/components/labels-management-table-actions/hooks';
import { LABELS_ACTION_BUTTON_LABELS } from 'src/common/components/labels-management-table-actions/labels';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { MOBILE_SDK_TESTS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';

import { SELECT_TESTS_PROMPT } from './MobileSdkTests.constants';
import { IMobileSdkDashboardTableProps } from './MobileSdkTests.types';
import { getMobileSdkTestsColumns, PLATFORM_TABLE_OPTIONS } from './MobileSdkTestsTableHelper';

import './MobileSdkTestsTable.scss';

export const MobileSdkTestsTable: FC<IMobileSdkDashboardTableProps> = ({
	testsQuery,
	unfilteredTotal,
	isBulkLabelEditEnabled,
	mobileCoreLabels,
	selectedRows,
	onSelectRow,
	onSelectAll,
	onClearSelection
}) => {
	const total: number = testsQuery.data?.total ?? 0;
	const { refetch } = testsQuery;
	const configuredValues = useMobileConfiguredValues();
	const columns = useMemo(
		() =>
			getMobileSdkTestsColumns(
				configuredValues.unitIdDisplayName,
				configuredValues.environmentDisplayName
			),
		[configuredValues.unitIdDisplayName, configuredValues.environmentDisplayName]
	);

	const isTableLoading =
		testsQuery.isLoading ||
		testsQuery.isPreviousData ||
		(testsQuery.isStale && testsQuery.isRefetching);

	const handleProcessingComplete = useCallback(async () => {
		await refetch();
		onClearSelection();
	}, [refetch, onClearSelection]);

	return (
		<EvSection
			ariaLabel="Mobile SDK tests table"
			skipLinkId="main-section"
			className="mobile-sdk-tests-table"
			dataAutomationId={MOBILE_SDK_DASHBOARD_PAGE_TABLE_SECTION_ID}
		>
			<EvTitle titleText="Uploaded tests" headingLevel={2} />

			{isBulkLabelEditEnabled && (
				<LabelsManagementTableActions<IMobileSdkTestRow>
					selectedRows={selectedRows}
					handleClearSelection={onClearSelection}
					promptText={SELECT_TESTS_PROMPT}
					itemName="test"
					addLabelButtonLabel={LABELS_ACTION_BUTTON_LABELS.ADD}
					changeLabelValueButtonLabel={LABELS_ACTION_BUTTON_LABELS.CHANGE}
					removeLabelsButtonLabel={LABELS_ACTION_BUTTON_LABELS.REMOVE}
					onLabelActionSuccess={handleProcessingComplete}
					tenantConfiguredLabels={mobileCoreLabels}
					configuredValues={configuredValues}
					appType="msdk"
				/>
			)}

			<PlatformTable
				isTableLoading={isTableLoading}
				tableId={MOBILE_SDK_TESTS_TABLE_ID}
				tableData={testsQuery.data?.rows}
				totalPaginationableResults={total}
				totalCount={unfilteredTotal}
				persistTableState
				columns={columns}
				options={{
					...PLATFORM_TABLE_OPTIONS,
					selectRow: isBulkLabelEditEnabled
						? {
								mode: 'checkbox',
								selected: selectedRows.map((r) => r.runId),
								onSelect: onSelectRow,
								onSelectAll
							}
						: undefined
				}}
			/>
		</EvSection>
	);
};
